// src/odoo/customers/api/productInsightsApi.ts
// All comments are in English.

import { http } from "../../../core/http";
import type { DateRangeQuery } from "./overviewApi";

// Base paths
const BASE_COMPANY = "/customers/company";
const BASE_CUSTOMER = "/customers";

// ---------- Company-level product insights (existing) ----------

export interface ProductInsightItem {
  product_id: number;
  product_name: string;
  gmv: number;
  order_count: number;
}

export interface ProductInsightCategoryItem {
  category_id: number | null;
  category_name: string | null;
  gmv: number;
  order_count: number;
}

export interface ProductInsightsResponse {
  company_partner_id: number;
  company_name: string;
  total_gmv: number;
  total_order_count: number;
  top_products: ProductInsightItem[];
  top_categories: ProductInsightCategoryItem[];
}

/**
 * Company-level product insights (used e.g. in overview side panels).
 */
export async function fetchProductInsights(
  companyPartnerId: number,
  params?: DateRangeQuery
): Promise<ProductInsightsResponse> {
  const res = await http.get<ProductInsightsResponse>(
    `${BASE_COMPANY}/${companyPartnerId}/product-insights`,
    { params }
  );
  return res.data;
}

// ---------- Customer Product Insights Tab (new) ----------
// These types mirror backend CustomerProduct*Response from customer_product_insights_schema.py

export interface CustomerProductSummary {
  customer_id: number;
  company_id: number;
  total_gmv: number;
  product_count: number;
  top_product_id: number | null;
  top_product_name: string | null;
  top_product_gmv: number | null;
  top_product_share: number | null; // 0-1
}

export interface CustomerProductPurchase {
  product_id: number;
  product_name: string;
  category_id: number | null;
  category_name: string | null;
  gmv: number;
  order_count: number;
  quantity: number;
  share: number; // 0-1
  first_purchase_date: string | null;
  last_purchase_date: string | null;
}

export interface CustomerProductPurchasesResponse {
  customer_id: number;
  company_id: number;
  total_gmv: number;
  total_order_count: number;
  products: CustomerProductPurchase[];
}

export interface CustomerProductRecommendation {
  product_id: number;
  product_name: string;
  brand_name: string | null;
  reason: string;

  // Cross-sell metrics
  co_count: number;
  support: number;
  confidence: number;
  lift: number;

  // Detailed counts from the strongest rule that leads to this recommendation
  total_orders: number | null;
  product_a_order_count: number | null;
  product_b_order_count: number | null;

  // Products that the customer already buys which trigger this recommendation
  trigger_product_ids: number[];
  trigger_product_names: string[];
}

export interface CustomerProductRecommendationsResponse {
  customer_id: number;
  company_id: number;
  recommendations: CustomerProductRecommendation[];
}

/**
 * Product summary for a company-level customer (Product Insights tab).
 */
export async function fetchCustomerProductSummary(
  companyPartnerId: number,
  params?: DateRangeQuery
): Promise<CustomerProductSummary> {
  const res = await http.get<CustomerProductSummary>(
    `${BASE_CUSTOMER}/${companyPartnerId}/product-summary`,
    { params }
  );
  return res.data;
}

/**
 * Product purchases distribution for a company-level customer.
 */
export async function fetchCustomerProductPurchases(
  companyPartnerId: number,
  params?: DateRangeQuery
): Promise<CustomerProductPurchasesResponse> {
  const res = await http.get<CustomerProductPurchasesResponse>(
    `${BASE_CUSTOMER}/${companyPartnerId}/product-purchases`,
    { params }
  );
  return res.data;
}

/**
 * Product recommendations (cross-sell based) for a company-level customer.
 */
export async function fetchCustomerProductRecommendations(
  companyPartnerId: number,
  params?: {
    companyId?: number | null;
    limit?: number;
    minLift?: number | null;
    minConfidence?: number | null;
    minSupport?: number | null;
  }
): Promise<CustomerProductRecommendationsResponse> {
  const { companyId, limit, minLift, minConfidence, minSupport } = params ?? {};

  const res = await http.get<CustomerProductRecommendationsResponse>(
    `${BASE_CUSTOMER}/${companyPartnerId}/product-recommendations`,
    {
      params: {
        company_id: companyId,
        limit,
        min_lift: minLift,
        min_confidence: minConfidence,
        min_support: minSupport,
      },
    }
  );
  return res.data;
}
